import { Orientation } from '@studio/design-system/layout';
import type { ReactElement } from 'react';
import { ReflexContainer, ReflexElement, ReflexSplitter } from 'react-reflex';
import { twMerge } from 'cn';
import { useLayoutPref } from './hooks.js';
import type { Props } from './types.js';

/** `<Scaffold>` alias. */
export { Scaffold as Root };

/** `<Scaffold>` component. */
export const Scaffold = ({
  bottom,
  children,
  className,
  header,
  left,
  right,
  top,
  ...props
}: Props): ReactElement => {
  const [layout, handleResize] = useLayoutPref();

  return (
    <div className={twMerge('grid h-screen grid-rows-[auto_1fr]', className)} {...props}>
      {header}

      <div className='overflow-hidden'>
        <ReflexContainer orientation={Orientation.Vertical} windowResizeAware>
          <ReflexElement
            className='z-10 shadow-[4px_4px_14px_rgba(0,0,0,0.25)]'
            maxSize={layout.left.maxWidth}
            minSize={layout.left.minWidth}
            onStopResize={handleResize}
            onResize={handleResize}
            size={layout.left.width}
          >
            <div className='h-full'>{left}</div>
          </ReflexElement>

          <ReflexSplitter className='!z-20 -ml-6 !w-6 !border-l-0 !border-base-100 !bg-transparent' />

          <ReflexElement>
            <div className='flex h-full flex-col'>
              <div>{top}</div>
              <div className='grow'>{children}</div>
              {bottom !== undefined && (
                <div style={{ minHeight: layout.bottom.height }}>{bottom}</div>
              )}
            </div>
          </ReflexElement>

          {right !== undefined && (
            <ReflexElement
              size={layout.right.width}
              style={{
                // Prevent the drawer from being clipped.
                overflow: 'unset'
              }}
            >
              <div className='h-full' style={{ width: layout.right.width }}>
                {right}
              </div>
            </ReflexElement>
          )}
        </ReflexContainer>
      </div>
    </div>
  );
};
